/**
 * How sure the detector was, as a word.
 *
 * ── Why there is no percentage anywhere ────────────────────────────────────
 *
 * The detector is a language model, and the figure on a detection is one it
 * reported about itself. A self-reported probability from a language model is
 * not calibrated: it tracks correctness weakly, and the difference between 90%
 * and 75% is not a difference anybody can act on. Printing it was a promise of
 * precision the number cannot keep - "94% sure" invites a supervisor to treat
 * one row as meaningfully surer than the next, and to argue about it in a
 * compliance review.
 *
 * So the model is asked for one of three words ([CONFIDENCE_BANDS], which is
 * the wire contract in `inference/schema.ts`), the record stores that word as
 * the number in [CONFIDENCE_VALUES] because both clients already had a numeric
 * column and a migration buys nothing here, and every surface reads it back
 * through [confidenceBand]. **The number is storage. The word is the record.**
 *
 * ── Why the thresholds are not the stored values ───────────────────────────
 *
 * A band's floor sits below the value it stores, not on it. Two reasons. Older
 * records hold figures a detector reported directly, before the bands existed,
 * and they have to land somewhere sensible rather than all in `low`. And a
 * floor equal to the stored value makes the round trip depend on float
 * equality, which is the kind of thing that works until somebody stores 0.8999.
 */

export const CONFIDENCE_BANDS = ["high", "medium", "low"] as const;

export type ConfidenceBand = (typeof CONFIDENCE_BANDS)[number];

/** The band the model reports, as the number the record stores. */
export const CONFIDENCE_VALUES: Record<ConfidenceBand, number> = {
  high: 0.9,
  medium: 0.75,
  low: 0.6,
};

/**
 * The floor of each band. Read in descending order - the first band whose floor
 * the value reaches wins, and `low` catches everything below `medium`.
 */
export const CONFIDENCE_FLOORS: Record<ConfidenceBand, number> = {
  high: 0.85,
  medium: 0.65,
  low: 0,
};

/** Sentence case, for a label or a fact. The plain word, with no figure. */
export const CONFIDENCE_LABELS: Record<ConfidenceBand, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
};

/**
 * Which band a stored confidence falls in.
 *
 * **Null in, null out, and that is the whole of the absent case.** The schema
 * stores null for a look nobody took (`detections_unassessed_has_no_confidence`
 * enforces it), and a band there would be a reading of low confidence rather
 * than the absence of one. A caller with a null has nothing to say about how
 * sure anything was, and every surface in both clients answers that by leaving
 * the confidence out - not by printing a sentence explaining the gap. What the
 * detector read is already on the row, in words, and "Not in shot" said twice
 * is not clearer than "Not in shot" said once.
 */
export function confidenceBand(value: number | null | undefined): ConfidenceBand | null {
  if (value == null || !Number.isFinite(value)) return null;
  if (value >= CONFIDENCE_FLOORS.high) return "high";
  if (value >= CONFIDENCE_FLOORS.medium) return "medium";
  return "low";
}

/** The word, or null where nothing was read. */
export function confidenceLabel(value: number | null | undefined): string | null {
  const band = confidenceBand(value);
  return band === null ? null : CONFIDENCE_LABELS[band];
}
